import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams } from "react-router-dom";
import { salesClientsApi, employeesApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import CountrySelector from "../components/CountrySelector";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import {
  Users,
  Plus,
  Search,
  Building2,
  Phone,
  Mail,
  MapPin,
  FileText,
  CreditCard,
  MessagesSquare,
  Pencil,
  Trash2,
  Globe,
  User,
  X,
  UserPlus,
  Clock,
  Key,
  Save,
  XCircle,
  Briefcase,
  ExternalLink,
  Link2,
  TrendingUp,
  Image as ImageIcon,
} from "lucide-react";
import api from "../lib/api";
import ClientMessagesTab from "../components/WepsterChat";
import { ClientTelegramSection } from "../components/TgChat";
import { cn } from "../lib/utils";
import ClientLogo from "../components/ClientLogo";
import ClientLogoLibrary from "../components/ClientLogoLibrary";

const fmtUsd = (n) => (n || n === 0 ? `$${Math.round(n).toLocaleString("en-US")}` : "—");

const TAG_STYLE = {
  sales: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  crm: "bg-violet-500/15 text-violet-300 border-violet-500/30",
  support: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  legacy: "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

const emptyPortfolioItem = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
  title: "",
  year: null,
  work_type: "",
  urls: [""],
  source: "manual",
});

// ===== Client profile dialog: Portfolio + Financials for one client =====
function ClientProfileDialog({ clientId, clientName, open, onClose, canEdit }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Editable copies
  const [items, setItems] = useState([]);
  const [caseLinks, setCaseLinks] = useState([]);
  const [website, setWebsite] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open || !clientId) return;
    setLoading(true);
    setProfile(null);
    setDirty(false);
    api
      .get(`/clients-database/${clientId}`)
      .then((res) => {
        setProfile(res.data);
        setItems(res.data.portfolio_items || []);
        setCaseLinks(res.data.case_study_links || []);
        setWebsite(res.data.client?.website || "");
      })
      .catch(() => toast.error("Failed to load client profile"))
      .finally(() => setLoading(false));
  }, [open, clientId]);

  const markDirty = () => setDirty(true);

  const savePortfolio = async () => {
    setSaving(true);
    try {
      const cleanItems = items
        .filter((i) => i.title?.trim() || (i.urls || []).some((u) => u?.trim()))
        .map(({ id, ...rest }) => ({
          ...(id && !String(id).startsWith("tmp-") ? { id } : {}),
          ...rest,
          year: rest.year ? parseInt(rest.year, 10) || null : null,
          urls: (rest.urls || []).filter((u) => u?.trim()),
        }));
      const cleanLinks = caseLinks
        .filter((l) => l.url?.trim())
        .map(({ id, ...rest }) => ({
          ...(id && !String(id).startsWith("tmp-") ? { id } : {}),
          ...rest,
        }));
      await api.put(`/clients-database/${clientId}/portfolio`, {
        portfolio_items: cleanItems,
        case_study_links: cleanLinks,
        website,
      });
      toast.success("Portfolio saved");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save portfolio");
    } finally {
      setSaving(false);
    }
  };

  const fin = profile?.financials;
  const projects = profile?.projects || [];
  const hasFinancials = fin && (fin.revenue_usd || 0) !== 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            {profile?.logo?.logo_url ? (
              <ClientLogo
                src={profile.logo.logo_url}
                variant={profile.logo.logo_variant}
                name={clientName}
                className="w-9 h-9"
              />
            ) : (
              <Briefcase className="h-5 w-5 text-amber-400" />
            )}
            {clientName}
            {profile?.client?.is_legacy && (
              <Badge className={cn("text-[10px] uppercase", TAG_STYLE.legacy)}>legacy</Badge>
            )}
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-zinc-500 py-8 text-center">Loading...</p>
        ) : !profile ? null : (
          <Tabs defaultValue="portfolio" className="mt-2">
            <TabsList className="bg-zinc-800 border-zinc-700">
              <TabsTrigger value="portfolio" className="data-[state=active]:bg-zinc-700">
                <Briefcase className="h-4 w-4 mr-2" />Portfolio
              </TabsTrigger>
              <TabsTrigger value="financials" className="data-[state=active]:bg-zinc-700">
                <TrendingUp className="h-4 w-4 mr-2" />Financials
              </TabsTrigger>
              <TabsTrigger value="billing" className="data-[state=active]:bg-zinc-700">
                <CreditCard className="h-4 w-4 mr-2" />Billing
              </TabsTrigger>
              <TabsTrigger value="messages" className="data-[state=active]:bg-zinc-700" data-testid="client-tab-messages">
                <MessagesSquare className="h-4 w-4 mr-2" />Messages
              </TabsTrigger>
            </TabsList>

            <TabsContent value="portfolio" className="space-y-4 mt-4">
              {/* Website */}
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-zinc-500 shrink-0" />
                <Input
                  value={website}
                  onChange={(e) => { setWebsite(e.target.value); markDirty(); }}
                  disabled={!canEdit}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-sm"
                  placeholder="Client website (https://...)"
                />
                {website?.trim() && (
                  <a href={website} target="_blank" rel="noreferrer" className="text-sky-400 hover:text-sky-300">
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
              </div>

              {/* Portal projects (auto) */}
              {projects.length > 0 && (
                <div>
                  <p className="text-xs text-zinc-500 uppercase mb-2">Portal projects · {projects.length}</p>
                  <div className="space-y-1">
                    {projects.map((p) => (
                      <div key={p.id} className="flex items-center gap-2 p-2 rounded bg-zinc-800/50 border border-zinc-800">
                        {p.cover_image ? (
                          <img src={p.cover_image} alt="" className="w-9 h-9 rounded object-cover shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded bg-zinc-700/50 flex items-center justify-center shrink-0">
                            <Building2 className="h-4 w-4 text-zinc-500" />
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-zinc-50 truncate">{p.name}</p>
                          <p className="text-[10px] text-zinc-500">{p.month || ""} · {p.status || ""}</p>
                        </div>
                        <span className="text-xs text-zinc-300 tabular-nums shrink-0">{fmtUsd(p.revenue_usd)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Works (imported + manual) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-zinc-500 uppercase">Works / renders · {items.length}</p>
                  {canEdit && (
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => { setItems([...items, emptyPortfolioItem()]); markDirty(); }}
                      className="h-6 px-2 text-[11px] text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                    >
                      <Plus className="h-3 w-3 mr-1" />Add work
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  {items.map((item, idx) => (
                    <div key={item.id} className="p-2 rounded bg-zinc-800/50 border border-zinc-800 space-y-1.5">
                      <div className="flex items-center gap-1.5">
                        <Input
                          value={item.title || ""}
                          onChange={(e) => { const a = [...items]; a[idx] = { ...item, title: e.target.value }; setItems(a); markDirty(); }}
                          disabled={!canEdit}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50 h-7 text-xs flex-1"
                          placeholder="Title"
                        />
                        <Input
                          value={item.year || ""}
                          onChange={(e) => { const a = [...items]; a[idx] = { ...item, year: e.target.value }; setItems(a); markDirty(); }}
                          disabled={!canEdit}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50 h-7 text-xs w-16 tabular-nums"
                          placeholder="Year"
                        />
                        <Input
                          value={item.work_type || ""}
                          onChange={(e) => { const a = [...items]; a[idx] = { ...item, work_type: e.target.value }; setItems(a); markDirty(); }}
                          disabled={!canEdit}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50 h-7 text-xs w-24"
                          placeholder="Type"
                        />
                        {canEdit && (
                          <Button
                            type="button" variant="ghost" size="icon"
                            onClick={() => { setItems(items.filter((_, i) => i !== idx)); markDirty(); }}
                            className="h-7 w-7 text-zinc-500 hover:text-red-400 shrink-0"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      {(item.urls || []).map((u, uidx) => (
                        <div key={uidx} className="flex items-center gap-1.5">
                          <Link2 className="h-3 w-3 text-zinc-600 shrink-0" />
                          <Input
                            value={u}
                            onChange={(e) => {
                              const a = [...items];
                              const urls = [...(item.urls || [])];
                              urls[uidx] = e.target.value;
                              a[idx] = { ...item, urls };
                              setItems(a); markDirty();
                            }}
                            disabled={!canEdit}
                            className="bg-zinc-900 border-zinc-700 text-zinc-300 h-6 text-[11px] flex-1"
                            placeholder="https://..."
                          />
                          {u?.trim() && (
                            <a href={u} target="_blank" rel="noreferrer" className="text-sky-400 hover:text-sky-300 shrink-0">
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </div>
                      ))}
                      {canEdit && (
                        <button
                          type="button"
                          onClick={() => { const a = [...items]; a[idx] = { ...item, urls: [...(item.urls || []), ""] }; setItems(a); markDirty(); }}
                          className="text-[10px] text-zinc-500 hover:text-sky-300"
                        >
                          + link
                        </button>
                      )}
                    </div>
                  ))}
                  {items.length === 0 && (
                    <p className="text-xs text-zinc-600 py-2">No works yet — add renders, tours, websites we made for this client.</p>
                  )}
                </div>
              </div>

              {/* Case studies */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-zinc-500 uppercase">Case studies · {caseLinks.length}</p>
                  {canEdit && (
                    <Button
                      type="button" variant="ghost" size="sm"
                      onClick={() => { setCaseLinks([...caseLinks, { id: `tmp-${Math.random().toString(36).slice(2, 10)}`, url: "", label: "" }]); markDirty(); }}
                      className="h-6 px-2 text-[11px] text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                    >
                      <Plus className="h-3 w-3 mr-1" />Add case link
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  {caseLinks.map((l, idx) => (
                    <div key={l.id || idx} className="flex items-center gap-1.5">
                      <Input
                        value={l.label || ""}
                        onChange={(e) => { const a = [...caseLinks]; a[idx] = { ...l, label: e.target.value }; setCaseLinks(a); markDirty(); }}
                        disabled={!canEdit}
                        className="bg-zinc-900 border-zinc-700 text-zinc-50 h-7 text-xs w-40"
                        placeholder="Label"
                      />
                      <Input
                        value={l.url || ""}
                        onChange={(e) => { const a = [...caseLinks]; a[idx] = { ...l, url: e.target.value }; setCaseLinks(a); markDirty(); }}
                        disabled={!canEdit}
                        className="bg-zinc-900 border-zinc-700 text-zinc-300 h-7 text-xs flex-1"
                        placeholder="https://case-study..."
                      />
                      {l.url?.trim() && (
                        <a href={l.url} target="_blank" rel="noreferrer" className="text-sky-400 hover:text-sky-300 shrink-0">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {canEdit && (
                        <Button
                          type="button" variant="ghost" size="icon"
                          onClick={() => { setCaseLinks(caseLinks.filter((_, i) => i !== idx)); markDirty(); }}
                          className="h-7 w-7 text-zinc-500 hover:text-red-400 shrink-0"
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  ))}
                  {caseLinks.length === 0 && (
                    <p className="text-xs text-zinc-600 py-1">Links to future case studies (Behance, site, drive).</p>
                  )}
                </div>
              </div>

              {canEdit && dirty && (
                <div className="flex justify-end pt-2 border-t border-zinc-800">
                  <Button onClick={savePortfolio} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-black h-8">
                    <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? "Saving..." : "Save portfolio"}
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="financials" className="space-y-4 mt-4">
              {!hasFinancials ? (
                <p className="text-sm text-zinc-500 py-6 text-center">
                  No financial records — {profile.client?.is_legacy ? "pre-portal (legacy) client." : "no billed services yet."}
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 rounded bg-zinc-800/50 border border-zinc-800">
                      <p className="text-[10px] text-zinc-500 uppercase">Revenue (USD eq.)</p>
                      <p className="text-xl font-bold text-zinc-50 tabular-nums">{fmtUsd(fin.revenue_usd)}</p>
                    </div>
                    <div className="p-3 rounded bg-zinc-800/50 border border-zinc-800">
                      <p className="text-[10px] text-zinc-500 uppercase">Paid</p>
                      <p className="text-xl font-bold text-emerald-400 tabular-nums">{fmtUsd(fin.paid_usd)}</p>
                    </div>
                    <div className="p-3 rounded bg-zinc-800/50 border border-zinc-800">
                      <p className="text-[10px] text-zinc-500 uppercase">Pending</p>
                      <p className="text-xl font-bold text-amber-400 tabular-nums">{fmtUsd(fin.pending_usd)}</p>
                    </div>
                  </div>

                  {Object.keys(fin.by_currency || {}).length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase mb-1">By currency (raw)</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(fin.by_currency).map(([cur, v]) => (
                          <Badge key={cur} variant="outline" className="bg-zinc-800 border-zinc-700 text-zinc-300 tabular-nums">
                            {cur}: {Math.round(v.paid).toLocaleString()} / {Math.round(v.total).toLocaleString()}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {Object.keys(fin.by_year || {}).length > 0 && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase mb-1">By year (USD eq., paid / billed)</p>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(fin.by_year).map(([year, v]) => (
                          <Badge key={year} variant="outline" className="bg-zinc-800 border-zinc-700 text-zinc-300 tabular-nums">
                            {year}: {fmtUsd(v.paid_usd)} / {fmtUsd(v.revenue_usd)}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-xs text-zinc-500 uppercase mb-1">Projects</p>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-zinc-800 hover:bg-transparent">
                          <TableHead className="text-zinc-500 text-xs h-8">Project</TableHead>
                          <TableHead className="text-zinc-500 text-xs h-8">Month</TableHead>
                          <TableHead className="text-zinc-500 text-xs h-8 text-right">Paid</TableHead>
                          <TableHead className="text-zinc-500 text-xs h-8 text-right">Billed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {projects.map((p) => (
                          <TableRow key={p.id} className="border-zinc-800">
                            <TableCell className="text-zinc-50 text-sm py-1.5">{p.name}</TableCell>
                            <TableCell className="text-zinc-400 text-xs py-1.5">{p.month || "—"}</TableCell>
                            <TableCell className="text-emerald-400 text-sm py-1.5 text-right tabular-nums">{fmtUsd(p.paid_usd)}</TableCell>
                            <TableCell className="text-zinc-300 text-sm py-1.5 text-right tabular-nums">{fmtUsd(p.revenue_usd)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Billing / requisites — the legal entities an invoice (рахунок)
                is issued to. Restored on the client card so a manager can read
                the EDRPOU / bank details without opening Edit → Billing. */}
            <TabsContent value="billing" className="space-y-3 mt-4">
              {(profile.client?.billing_profiles || []).length === 0 ? (
                <div className="py-6 text-center space-y-2">
                  <p className="text-sm text-zinc-500">
                    No billing profile yet — an invoice can't be issued to this client
                    until its legal requisites are filled in.
                  </p>
                  {canEdit && (
                    <p className="text-xs text-zinc-600">
                      Add one via <span className="text-zinc-400">Edit client → Billing</span>.
                    </p>
                  )}
                </div>
              ) : (
                (profile.client.billing_profiles || []).map((bp, idx) => (
                  <div
                    key={bp.id || idx}
                    className={cn(
                      "rounded-lg border p-3 space-y-2",
                      bp.is_default ? "border-sky-700/60 bg-sky-900/10" : "border-zinc-800 bg-zinc-800/30"
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-sky-400 shrink-0" />
                      <span className="text-sm font-medium text-zinc-50">
                        {bp.label || bp.legal_name || `Profile ${idx + 1}`}
                      </span>
                      {bp.is_default && (
                        <Badge className="text-[10px] uppercase bg-sky-500/15 text-sky-300 border-sky-500/30">default</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                      {[
                        ["Legal name", bp.legal_name],
                        ["EDRPOU / Tax ID", bp.edrpou],
                        ["Legal address", bp.billing_address],
                        ["Bank", bp.bank_name],
                        ["MFO / SWIFT", bp.mfo],
                        ["IBAN / Account", bp.iban],
                        ["SWIFT (foreign)", bp.swift],
                      ]
                        .filter(([, v]) => v && String(v).trim())
                        .map(([label, value]) => (
                          <div key={label} className={label === "Legal address" || label === "Legal name" ? "col-span-2" : ""}>
                            <p className="text-[10px] text-zinc-500 uppercase">{label}</p>
                            <p className="text-zinc-200 break-words">{value}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                ))
              )}
            </TabsContent>

            <TabsContent value="messages" className="mt-4">
              <ClientTelegramSection clientId={clientId} canWrite={canEdit} />
              <ClientMessagesTab clientId={clientId} canWrite={canEdit} />
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

// What the client's login actually looks like, in words a manager can act on.
const PORTAL_STATE = {
  ready: { label: "Login works — email and password are set", cls: "text-green-500" },
  deactivated: { label: "Account is deactivated — saving credentials here will reactivate it", cls: "text-amber-400" },
  access_disabled: { label: "Portal access is off for this account — saving will turn it on", cls: "text-amber-400" },
  invite_pending: { label: "Invite sent, password not set yet", cls: "text-sky-400" },
  no_password: { label: "No password yet — send an invite or set one below", cls: "text-amber-400" },
  no_account: { label: "No login account yet — saving will create one", cls: "text-zinc-500" },
  other_role: { label: "This email belongs to a staff account — use another address", cls: "text-red-400" },
  no_email: { label: "No email on the card", cls: "text-zinc-500" },
};

const emptyClient = {
  send_invite: true,
  name: "",
  country: "",
  // "" = decide from the country; "uk"/"en" = the client asked for this one.
  portal_language: "",
  contact_person: "",
  phone: "",
  email: "",
  // Display-only company name (kept for back-compat); legal data lives on billing_profiles.
  company_name: "",
  address: "",
  notes: "",
  // SLA settings
  response_time_hours: 48,
  penalty_rate_per_day: 0,
  // Portal access
  portal_password: "",
  // Multi-entity billing
  billing_profiles: [],
};

const emptyBillingProfile = () => ({
  id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
  label: "",
  legal_name: "",
  edrpou: "",
  billing_address: "",
  bank_name: "",
  mfo: "",
  iban: "",
  swift: "",
  bank_account: "",
  is_default: false,
});

export default function ClientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [formData, setFormData] = useState(emptyClient);
  const [portalStatus, setPortalStatus] = useState(null);
  
  // Portal Users state
  const [portalUsers, setPortalUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [loadingAvailableUsers, setLoadingAvailableUsers] = useState(false);
  
  // Create new portal user state
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserData, setNewUserData] = useState({ name: "", email: "", password: "", role: "client" });
  const [creatingUser, setCreatingUser] = useState(false);
  
  // Email logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [selectedUserForLogs, setSelectedUserForLogs] = useState(null);
  
  // Password editing state
  const [editingPasswordUserId, setEditingPasswordUserId] = useState(null);
  const [newPasswordValue, setNewPasswordValue] = useState("");

  // Client-type filter — "any" / "sales" / "crm" / "support" / "legacy". Persists so
  // a manager who lives in one type doesn't have to re-filter every visit.
  const [typeFilter, setTypeFilter] = useState(() => {
    if (typeof window === "undefined") return "any";
    return window.localStorage.getItem("clients_type_filter") || "any";
  });

  // Rollups from /clients-database: id -> {projects_count, portfolio_count, revenue, years...}
  const [rollups, setRollups] = useState({});
  // Profile dialog (Portfolio + Financials)
  const [profileClient, setProfileClient] = useState(null);
  // Deep link /clients?client=<id> — the Wepster lead card links here.
  const [searchParams] = useSearchParams();
  const deepClientId = searchParams.get("client");
  useEffect(() => {
    if (!deepClientId || profileClient) return;
    const c = clients.find((x) => x.id === deepClientId);
    if (c) setProfileClient(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepClientId, clients]);
  // Logo library — the marks that have no client record live only here
  const [logoLibraryOpen, setLogoLibraryOpen] = useState(false);
  
  const fetchEmailLogs = async (userId) => {
    try {
      const res = await api.get(`/admin/email-logs?user_id=${userId}`);
      setEmailLogs(res.data || []);
    } catch {
      setEmailLogs([]);
    }
  };

  useEffect(() => {
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter]);

  const fetchRollups = () => {
    api
      .get("/clients-database")
      .then((res) => {
        const map = {};
        for (const r of res.data?.clients || []) map[r.id] = r;
        setRollups(map);
      })
      .catch(() => {});
  };

  useEffect(() => {
    fetchRollups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("clients_type_filter", typeFilter);
    }
  }, [typeFilter]);

  const fetchClients = async () => {
    try {
      const res = await salesClientsApi.getAll(typeFilter);
      setClients(res.data || []);
    } catch (error) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  // Per-tab counts derived from the loaded list (when typeFilter="any" we have
  // the full picture; on a single-tag view counts for other tags are unknown
  // and shown as "—").
  const typeCounts = useMemo(() => {
    if (typeFilter !== "any") {
      return { any: clients.length, sales: null, crm: null, support: null, legacy: null };
    }
    const counts = { any: clients.length, sales: 0, crm: 0, support: 0, legacy: 0 };
    for (const c of clients) {
      const tags = c.client_types || [c.type].flat().filter(Boolean) || [];
      for (const t of tags) {
        if (counts[t] !== undefined) counts[t] += 1;
      }
    }
    return counts;
  }, [clients, typeFilter]);

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error("Client name is required");
      return;
    }

    try {
      const submitData = { ...formData };
      // Two intents, never both: email them a set-password link, or set one
      // here. The flag has to reach the backend — dropping it is why ticking
      // "send invite" on an existing client sent nothing. backlog 5d757da7.
      submitData.send_invite = formData.send_invite !== false;
      if (!submitData.send_invite && formData.client_password) {
        submitData.portal_password = formData.client_password;
      }
      delete submitData.client_password;
      // Strip client-side tmp ids on billing profiles so the server assigns stable UUIDs
      if (Array.isArray(submitData.billing_profiles)) {
        submitData.billing_profiles = submitData.billing_profiles.map(({ id, ...rest }) => {
          const cleanId = typeof id === "string" && id.startsWith("tmp-") ? undefined : id;
          return cleanId ? { id: cleanId, ...rest } : rest;
        });
      }
      
      if (editingClient) {
        await salesClientsApi.update(editingClient.id, submitData);
        toast.success("Client updated");
      } else {
        await salesClientsApi.create(submitData);
        toast.success("Client created");
      }
      setDialogOpen(false);
      setEditingClient(null);
      setFormData(emptyClient);
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save client");
    }
  };

  const fetchPortalStatus = async (clientId) => {
    try {
      const r = await api.get(`/sales-clients/${clientId}/portal-status`);
      setPortalStatus(r.data);
    } catch (_) {
      setPortalStatus(null);
    }
  };

  const handleEdit = async (client) => {
    setEditingClient(client);
    // Server returns billing_profiles for new and migrated clients. If empty, seed
    // a single profile from any legacy top-level fields so the UI always has at
    // least one editable card on the Billing tab.
    let profiles = Array.isArray(client.billing_profiles) ? [...client.billing_profiles] : [];
    if (profiles.length === 0) {
      const hasLegacy = client.legal_name || client.edrpou || client.iban || client.bank_name || client.mfo || client.bank_account || client.billing_address;
      if (hasLegacy) {
        profiles = [{
          id: `tmp-${Math.random().toString(36).slice(2, 10)}`,
          label: client.legal_name || client.company_name || "Default",
          legal_name: client.legal_name || "",
          edrpou: client.edrpou || "",
          billing_address: client.billing_address || client.address || "",
          bank_name: client.bank_name || "",
          mfo: client.mfo || "",
          iban: client.iban || "",
          swift: client.bank_swift || "",
          bank_account: client.bank_account || "",
          is_default: true,
        }];
      }
    }
    setPortalStatus(null);
    fetchPortalStatus(client.id);
    setFormData({
      send_invite: true,
      name: client.name || "",
      country: client.country || "",
      portal_language: client.portal_language || "",
      contact_person: client.contact_person || "",
      phone: client.phone || "",
      email: client.email || "",
      company_name: client.company_name || "",
      address: client.address || "",
      notes: client.notes || "",
      response_time_hours: client.response_time_hours || 48,
      penalty_rate_per_day: client.penalty_rate_per_day || 0,
      portal_password: "", // Don't show existing password
      billing_profiles: profiles,
    });
    
    // Load portal users for this client
    try {
      const res = await salesClientsApi.get(client.id);
      setPortalUsers(res.data?.portal_users || []);
    } catch (error) {
      setPortalUsers([]);
    }
    
    setDialogOpen(true);
  };
  
  // Search available users for adding to client
  const searchAvailableUsers = async (query) => {
    if (!editingClient) return;
    setLoadingAvailableUsers(true);
    try {
      const res = await salesClientsApi.getAvailableUsers(editingClient.id, query);
      setAvailableUsers(res.data || []);
    } catch (error) {
      setAvailableUsers([]);
    } finally {
      setLoadingAvailableUsers(false);
    }
  };
  
  // Add user to portal users
  const handleAddPortalUser = async (userId) => {
    if (!editingClient) return;
    const newUserIds = [...(portalUsers.map(u => u.id)), userId];
    try {
      await salesClientsApi.update(editingClient.id, { user_ids: newUserIds });
      // Refresh portal users
      const res = await salesClientsApi.get(editingClient.id);
      setPortalUsers(res.data?.portal_users || []);
      setAvailableUsers(availableUsers.filter(u => u.id !== userId));
      toast.success("User added");
    } catch (error) {
      toast.error("Failed to add user");
    }
  };
  
  // Remove user from portal users
  const handleRemovePortalUser = async (userId) => {
    if (!editingClient) return;
    const newUserIds = portalUsers.filter(u => u.id !== userId).map(u => u.id);
    try {
      await salesClientsApi.update(editingClient.id, { user_ids: newUserIds });
      setPortalUsers(portalUsers.filter(u => u.id !== userId));
      toast.success("User removed");
    } catch (error) {
      toast.error("Failed to remove user");
    }
  };
  
  // Create new portal user and add to client
  const handleCreatePortalUser = async () => {
    if (!editingClient) return;
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      toast.error("Please fill all required fields");
      return;
    }
    if (newUserData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setCreatingUser(true);
    try {
      const response = await employeesApi.createPortalUser({
        name: newUserData.name,
        email: newUserData.email,
        password: newUserData.password,
        role: newUserData.role,
        client_id: editingClient.id
      });
      toast.success("User created and added to client");
      
      // Refresh portal users
      const res = await salesClientsApi.get(editingClient.id);
      setPortalUsers(res.data?.portal_users || []);
      
      // Reset form
      setNewUserData({ name: "", email: "", password: "", role: "client" });
      setShowCreateUser(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || "Failed to create user");
    } finally {
      setCreatingUser(false);
    }
  };

  const handleDelete = async (clientId) => {
    try {
      await salesClientsApi.delete(clientId);
      toast.success("Client deleted");
      fetchClients();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete client");
    }
  };

  const filteredClients = clients.filter(c =>
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.country?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canDelete = user?.role === "admin" || user?.role === "sales_director";

  return (
    <div className="space-y-6" data-testid="clients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 flex items-center gap-3">
            <Users className="h-7 w-7 text-sky-400" />
            Client Management
          </h1>
          <p className="text-zinc-400 mt-1">Unified client database — contacts, billing, portfolio and financials, including legacy clients</p>
        </div>
        <div className="flex items-center gap-2">
        <Button
          variant="outline"
          className="border-zinc-700"
          onClick={() => setLogoLibraryOpen(true)}
          data-testid="client-logos-btn"
        >
          <ImageIcon className="h-4 w-4 mr-2" />Logos
        </Button>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingClient(null);
            setFormData(emptyClient);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-sky-500 hover:bg-sky-600" data-testid="add-client-btn">
              <Plus className="h-4 w-4 mr-2" />Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-zinc-50">
                {editingClient ? "Edit Client" : "Add New Client"}
              </DialogTitle>
            </DialogHeader>
            <Tabs defaultValue="basic" className="mt-4">
              <TabsList className="bg-zinc-800 border-zinc-700">
                <TabsTrigger value="basic" className="data-[state=active]:bg-zinc-700">
                  <User className="h-4 w-4 mr-2" />Basic
                </TabsTrigger>
                <TabsTrigger value="contact" className="data-[state=active]:bg-zinc-700">
                  <Phone className="h-4 w-4 mr-2" />Contact
                </TabsTrigger>
                <TabsTrigger value="billing" className="data-[state=active]:bg-zinc-700">
                  <CreditCard className="h-4 w-4 mr-2" />Billing
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Client Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      placeholder="Display name"
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Country</Label>
                    <CountrySelector
                      value={formData.country}
                      onChange={(country) => setFormData({ ...formData, country })}
                      placeholder="Select country..."
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Portal language</Label>
                    <select
                      value={formData.portal_language || ""}
                      onChange={(e) => setFormData({ ...formData, portal_language: e.target.value })}
                      className="w-full h-10 rounded-md bg-zinc-800 border border-zinc-700 text-zinc-50 text-sm px-3"
                      data-testid="client-portal-language"
                    >
                      <option value="">Auto (by country)</option>
                      <option value="uk">Ukrainian</option>
                      <option value="en">English</option>
                    </select>
                    <p className="text-[10px] text-zinc-500 mt-1">
                      Language of the client&rsquo;s own cabinet. Auto = Ukrainian for Ukraine, English for everyone else.
                    </p>
                  </div>
                </div>
                <p className="text-[11px] text-zinc-500">
                  Legal company details (legal name, EDRPOU, bank, IBAN) live on the Billing tab — one client can have multiple legal entities.
                </p>
                <div>
                  <Label className="text-zinc-300">Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    placeholder="Additional notes about the client"
                    rows={3}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="contact" className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Contact Person</Label>
                  <Input
                    value={formData.contact_person}
                    onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    placeholder="Primary contact name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-300">Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      placeholder="email@company.com"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-300">Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-zinc-50"
                      placeholder="+1 234 567 890"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-300">Address</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    placeholder="Full address"
                    rows={2}
                  />
                </div>
                
                {/* Portal Access - inside Contact tab */}
                <div className="pt-4 border-t border-zinc-800">
                  <Label className="text-zinc-300 text-sm font-medium mb-2 block">Client Portal Access</Label>
                  <div>
                    {formData.email ? (
                      <>
                        <div className="flex items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            id="send-invite"
                            checked={formData.send_invite !== false}
                            onChange={(e) => setFormData({ ...formData, send_invite: e.target.checked })}
                            className="rounded border-zinc-600"
                            data-testid="client-send-invite"
                          />
                          <Label htmlFor="send-invite" className="text-zinc-400 text-xs cursor-pointer">
                            Send invite email to set password
                          </Label>
                        </div>
                        {!formData.send_invite && (
                          <div className="mb-2">
                            <Label className="text-zinc-400 text-xs mb-1 block">Set password manually</Label>
                            <Input
                              type="password"
                              value={formData.client_password || ""}
                              onChange={(e) => setFormData({ ...formData, client_password: e.target.value })}
                              placeholder="Enter password for client portal"
                              className="bg-zinc-800 border-zinc-700 text-zinc-50 h-8 text-xs"
                              data-testid="client-password-input"
                            />
                          </div>
                        )}
                        <p className="text-[10px] text-zinc-500">
                          {formData.send_invite !== false
                            ? <>Invite will be sent to: <span className="text-zinc-300">{formData.email}</span></>
                            : <>Password applies to: <span className="text-zinc-300">{formData.email}</span></>}
                        </p>
                        {/* The real state of the login behind this email. The flat
                            "Portal access already enabled" came from a flag on the
                            client and said nothing about the account — which could
                            be deactivated, as it was for the client who could not
                            log in. backlog 5d757da7. */}
                        {editingClient && portalStatus && (
                          <p className={`text-[10px] mt-1 ${PORTAL_STATE[portalStatus.state]?.cls || "text-zinc-500"}`}>
                            {PORTAL_STATE[portalStatus.state]?.label || portalStatus.state}
                          </p>
                        )}
                      </>
                    ) : (
                      <p className="text-[10px] text-zinc-500">Enter email above to enable portal access</p>
                    )}
                  </div>
                </div>
                
                {/* Portal Users - Multi-user access */}
                {editingClient && (
                  <div className="pt-4 border-t border-zinc-800">
                    <Label className="text-zinc-300 text-sm font-medium mb-3 block flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Portal Users ({portalUsers.length})
                    </Label>
                    <p className="text-[10px] text-zinc-500 mb-3">
                      Users with portal access to this client's projects
                    </p>
                    
                    {/* Current portal users */}
                    {portalUsers.length > 0 && (
                      <div className="space-y-2 mb-3">
                        {portalUsers.map((u) => (
                          <div 
                            key={u.id} 
                            className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-md border border-zinc-700"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-orange-500/20 flex items-center justify-center">
                                <User className="h-3.5 w-3.5 text-orange-400" />
                              </div>
                              <div>
                                <p className="text-sm text-zinc-50">{u.name}</p>
                                <p className="text-[10px] text-zinc-500">{u.email}</p>
                              </div>
                              <Badge className="text-[10px] bg-zinc-700 text-zinc-300">{u.role}</Badge>
                              {u.invite_accepted_at && <Badge className="text-[10px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">Active</Badge>}
                              {!u.invite_accepted_at && !u.password && <Badge className="text-[10px] bg-amber-500/15 text-amber-400 border-amber-500/20">Invite pending</Badge>}
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    await api.post(`/admin/resend-client-invite/${u.id}`);
                                    toast.success(`Invite resent to ${u.email}`);
                                    fetchEmailLogs(u.id);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || "Failed to resend invite");
                                  }
                                }}
                                className="h-7 px-2 text-[10px] text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                                data-testid={`resend-invite-${u.id}`}
                              >
                                <Mail className="h-3 w-3 mr-1" /> Resend
                              </Button>
                              {editingPasswordUserId === u.id ? (
                                <div className="flex items-center gap-1">
                                  <Input
                                    type="password"
                                    value={newPasswordValue}
                                    onChange={(e) => setNewPasswordValue(e.target.value)}
                                    placeholder="New password"
                                    className="bg-zinc-900 border-zinc-700 text-zinc-50 h-7 w-28 text-[10px]"
                                    data-testid={`password-input-${u.id}`}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={async () => {
                                      if (!newPasswordValue || newPasswordValue.length < 4) {
                                        toast.error("Min 4 characters");
                                        return;
                                      }
                                      try {
                                        await api.put(`/admin/set-client-password/${u.id}`, { password: newPasswordValue });
                                        toast.success(`Password updated for ${u.email}`);
                                        setEditingPasswordUserId(null);
                                        setNewPasswordValue("");
                                      } catch (e) {
                                        toast.error(e.response?.data?.detail || "Failed");
                                      }
                                    }}
                                    className="h-7 px-1.5 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                                    data-testid={`save-password-${u.id}`}
                                  >
                                    <Save className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => { setEditingPasswordUserId(null); setNewPasswordValue(""); }}
                                    className="h-7 px-1.5 text-zinc-400 hover:text-red-400"
                                  >
                                    <XCircle className="h-3 w-3" />
                                  </Button>
                                </div>
                              ) : (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => { setEditingPasswordUserId(u.id); setNewPasswordValue(""); }}
                                  className="h-7 px-2 text-[10px] text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                                  data-testid={`change-password-${u.id}`}
                                >
                                  <Key className="h-3 w-3 mr-1" /> Password
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedUserForLogs(selectedUserForLogs === u.id ? null : u.id);
                                  if (selectedUserForLogs !== u.id) fetchEmailLogs(u.id);
                                }}
                                className="h-7 px-2 text-[10px] text-zinc-400 hover:text-zinc-300"
                                data-testid={`email-logs-${u.id}`}
                              >
                                <Clock className="h-3 w-3 mr-1" /> Log
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemovePortalUser(u.id)}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                        {/* Email Log Panel */}
                        {selectedUserForLogs && emailLogs.length > 0 && (
                          <div className="bg-zinc-900/80 rounded-lg border border-zinc-700 p-3 mt-2">
                            <p className="text-xs text-zinc-400 font-medium mb-2 flex items-center gap-1.5"><Mail className="h-3 w-3" /> Email History</p>
                            <div className="space-y-1.5 max-h-40 overflow-y-auto">
                              {emailLogs.map((log) => (
                                <div key={log.id} className="flex items-center justify-between text-[10px] px-2 py-1.5 rounded bg-zinc-800/50">
                                  <div className="flex items-center gap-2">
                                    <span className={cn("inline-block w-1.5 h-1.5 rounded-full",
                                      log.status === "sent" && "bg-emerald-400",
                                      log.status === "failed" && "bg-red-400",
                                      log.status === "skipped" && "bg-yellow-400",
                                      log.status === "pending" && "bg-zinc-400"
                                    )} />
                                    <span className="text-zinc-300">{log.subject}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className={cn(
                                      log.status === "sent" ? "text-emerald-400" : log.status === "failed" ? "text-red-400" : "text-zinc-500"
                                    )}>{log.status}</span>
                                    <span className="text-zinc-600">{log.sent_at?.slice(0, 16).replace("T", " ")}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {emailLogs.some(l => l.error) && (
                              <p className="text-[10px] text-red-400 mt-2">Last error: {emailLogs.find(l => l.error)?.error}</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Add user search */}
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          value={userSearchQuery}
                          onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            searchAvailableUsers(e.target.value);
                          }}
                          className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
                          placeholder="Search users to add..."
                        />
                      </div>
                      
                      {/* Available users dropdown */}
                      {userSearchQuery && (
                        <div className="border border-zinc-700 rounded-md bg-zinc-800/50 max-h-40 overflow-y-auto">
                          {loadingAvailableUsers ? (
                            <p className="text-xs text-zinc-500 p-2">Searching...</p>
                          ) : availableUsers.length > 0 ? (
                            availableUsers.map((u) => (
                              <button
                                key={u.id}
                                type="button"
                                onClick={() => {
                                  handleAddPortalUser(u.id);
                                  setUserSearchQuery("");
                                }}
                                className="w-full flex items-center justify-between p-2 hover:bg-zinc-700/50 text-left"
                              >
                                <div className="flex items-center gap-2">
                                  <UserPlus className="h-3.5 w-3.5 text-green-400" />
                                  <span className="text-sm text-zinc-50">{u.name}</span>
                                  <span className="text-[10px] text-zinc-500">{u.email}</span>
                                </div>
                                <Badge className="text-[10px] bg-zinc-700 text-zinc-300">{u.role}</Badge>
                              </button>
                            ))
                          ) : (
                            <p className="text-xs text-zinc-500 p-2">No users found (must have role partner/client with portal access)</p>
                          )}
                        </div>
                      )}
                      
                      {/* Create new user button/form */}
                      {!showCreateUser ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCreateUser(true)}
                          className="w-full border-dashed border-zinc-700 text-zinc-400 hover:text-zinc-50 hover:border-green-500/50 hover:bg-green-500/10"
                        >
                          <UserPlus className="h-3.5 w-3.5 mr-2" />
                          Create New User
                        </Button>
                      ) : (
                        <div className="p-3 border border-green-800/30 bg-green-900/10 rounded-md space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-green-400 text-xs font-medium">Create New Portal User</Label>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setShowCreateUser(false);
                                setNewUserData({ name: "", email: "", password: "", role: "client" });
                              }}
                              className="h-6 w-6 p-0 text-zinc-500 hover:text-zinc-50"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              value={newUserData.name}
                              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50 text-xs h-8"
                              placeholder="Full name *"
                            />
                            <Input
                              type="email"
                              value={newUserData.email}
                              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50 text-xs h-8"
                              placeholder="Email *"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              type="password"
                              value={newUserData.password}
                              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                              className="bg-zinc-800 border-zinc-700 text-zinc-50 text-xs h-8"
                              placeholder="Password (min 6) *"
                            />
                            <select
                              value={newUserData.role}
                              onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                              className="bg-zinc-800 border border-zinc-700 text-zinc-50 text-xs h-8 rounded-md px-2"
                            >
                              <option value="client">Client</option>
                              <option value="partner">Partner</option>
                            </select>
                          </div>
                          <Button
                            type="button"
                            onClick={handleCreatePortalUser}
                            disabled={creatingUser}
                            className="w-full bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                          >
                            {creatingUser ? "Creating..." : "Create & Add User"}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="billing" className="space-y-4 mt-4">
                <div className="p-3 bg-zinc-800/50 rounded-lg border border-zinc-700/50 mb-2">
                  <p className="text-xs text-zinc-400">
                    Billing profiles are used to generate invoices (Rakhunok) and acts. Holdings with several legal entities can add multiple profiles — pick which one to bill from when creating a payment.
                  </p>
                </div>

                {(formData.billing_profiles || []).length === 0 && (
                  <div className="p-4 border border-dashed border-zinc-700 rounded-md text-center">
                    <p className="text-sm text-zinc-400 mb-3">No billing profiles yet</p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const p = emptyBillingProfile();
                        p.is_default = true;
                        setFormData({ ...formData, billing_profiles: [p] });
                      }}
                      className="border-sky-700 text-sky-300 hover:bg-sky-500/10"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1.5" /> Add billing profile
                    </Button>
                  </div>
                )}

                {(formData.billing_profiles || []).map((profile, idx) => (
                  <div
                    key={profile.id || idx}
                    className={cn(
                      "p-3 rounded-md border bg-zinc-800/30 space-y-3",
                      profile.is_default ? "border-sky-700/60" : "border-zinc-700"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Building2 className="h-4 w-4 text-sky-400 shrink-0" />
                        <Input
                          value={profile.label || ""}
                          onChange={(e) => {
                            const profiles = [...formData.billing_profiles];
                            profiles[idx] = { ...profile, label: e.target.value };
                            setFormData({ ...formData, billing_profiles: profiles });
                          }}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50 h-8 text-sm"
                          placeholder="Profile label (e.g. main entity, FOP, holding LLC)"
                        />
                        {profile.is_default ? (
                          <Badge className="bg-sky-500/15 text-sky-300 border-sky-500/30 text-[10px]">Default</Badge>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const profiles = formData.billing_profiles.map((p, i) => ({ ...p, is_default: i === idx }));
                              setFormData({ ...formData, billing_profiles: profiles });
                            }}
                            className="h-7 text-[10px] text-zinc-400 hover:text-sky-300 hover:bg-sky-500/10"
                          >
                            Make default
                          </Button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          let profiles = formData.billing_profiles.filter((_, i) => i !== idx);
                          // Re-promote first remaining if we deleted the default
                          if (profile.is_default && profiles.length > 0) {
                            profiles = profiles.map((p, i) => ({ ...p, is_default: i === 0 }));
                          }
                          setFormData({ ...formData, billing_profiles: profiles });
                        }}
                        className="h-7 w-7 text-zinc-400 hover:text-red-400"
                        title="Delete profile"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Legal name (for documents)</Label>
                        <Input
                          value={profile.legal_name || ""}
                          onChange={(e) => {
                            const profiles = [...formData.billing_profiles];
                            profiles[idx] = { ...profile, legal_name: e.target.value };
                            setFormData({ ...formData, billing_profiles: profiles });
                          }}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50"
                          placeholder="LLC / TOV / FOP full legal name"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">EDRPOU / Tax ID</Label>
                        <Input
                          value={profile.edrpou || ""}
                          onChange={(e) => {
                            const profiles = [...formData.billing_profiles];
                            profiles[idx] = { ...profile, edrpou: e.target.value };
                            setFormData({ ...formData, billing_profiles: profiles });
                          }}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50"
                          placeholder="EDRPOU / IPN code"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-zinc-400 text-xs">Legal address</Label>
                      <Input
                        value={profile.billing_address || ""}
                        onChange={(e) => {
                          const profiles = [...formData.billing_profiles];
                          profiles[idx] = { ...profile, billing_address: e.target.value };
                          setFormData({ ...formData, billing_profiles: profiles });
                        }}
                        className="bg-zinc-900 border-zinc-700 text-zinc-50"
                        placeholder="Full legal address"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Bank name</Label>
                        <Input
                          value={profile.bank_name || ""}
                          onChange={(e) => {
                            const profiles = [...formData.billing_profiles];
                            profiles[idx] = { ...profile, bank_name: e.target.value };
                            setFormData({ ...formData, billing_profiles: profiles });
                          }}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50"
                          placeholder="Bank name"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">MFO / SWIFT</Label>
                        <Input
                          value={profile.mfo || ""}
                          onChange={(e) => {
                            const profiles = [...formData.billing_profiles];
                            profiles[idx] = { ...profile, mfo: e.target.value };
                            setFormData({ ...formData, billing_profiles: profiles });
                          }}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50"
                          placeholder="MFO code (UA) or SWIFT"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">IBAN / Account</Label>
                        <Input
                          value={profile.iban || ""}
                          onChange={(e) => {
                            const profiles = [...formData.billing_profiles];
                            profiles[idx] = { ...profile, iban: e.target.value };
                            setFormData({ ...formData, billing_profiles: profiles });
                          }}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50"
                          placeholder="UA..."
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">SWIFT (foreign banks)</Label>
                        <Input
                          value={profile.swift || ""}
                          onChange={(e) => {
                            const profiles = [...formData.billing_profiles];
                            profiles[idx] = { ...profile, swift: e.target.value };
                            setFormData({ ...formData, billing_profiles: profiles });
                          }}
                          className="bg-zinc-900 border-zinc-700 text-zinc-50"
                          placeholder="SWIFT/BIC"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {(formData.billing_profiles || []).length > 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const profiles = [...(formData.billing_profiles || []), emptyBillingProfile()];
                      setFormData({ ...formData, billing_profiles: profiles });
                    }}
                    className="w-full border-dashed border-zinc-700 text-zinc-400 hover:text-sky-300 hover:border-sky-500/50 hover:bg-sky-500/10"
                  >
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add another billing profile
                  </Button>
                )}
              </TabsContent>
            </Tabs>

            {/* SLA Settings */}
            <div className="pt-4 border-t border-zinc-800 mt-4">
              <Label className="text-zinc-300 text-sm font-medium mb-3 block">SLA Settings</Label>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs">Response Time (hours)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.response_time_hours}
                    onChange={(e) => setFormData({ ...formData, response_time_hours: parseInt(e.target.value) || 48 })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    placeholder="48"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">Hours to respond to client requests</p>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Penalty Rate (% per day)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.1"
                    value={formData.penalty_rate_per_day}
                    onChange={(e) => setFormData({ ...formData, penalty_rate_per_day: parseFloat(e.target.value) || 0 })}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50"
                    placeholder="0"
                  />
                  <p className="text-[10px] text-zinc-500 mt-1">Penalty % charged per day overdue</p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="bg-sky-500 hover:bg-sky-600" data-testid="save-client-btn">
                {editingClient ? "Update" : "Create"} Client
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-sky-400" />
              <span className="text-xs text-zinc-500 uppercase">Total Clients</span>
            </div>
            <p className="text-2xl font-bold text-zinc-50">{clients.length}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Globe className="h-4 w-4 text-purple-400" />
              <span className="text-xs text-zinc-500 uppercase">Countries</span>
            </div>
            <p className="text-2xl font-bold text-zinc-50">
              {new Set(clients.map(c => c.country).filter(Boolean)).size}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <Briefcase className="h-4 w-4 text-amber-400" />
              <span className="text-xs text-zinc-500 uppercase">With Portfolio</span>
            </div>
            <p className="text-2xl font-bold text-zinc-50">
              {clients.filter(c => (rollups[c.id]?.portfolio_count || 0) > 0 || (rollups[c.id]?.projects_count || 0) > 0).length}
            </p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-emerald-400" />
              <span className="text-xs text-zinc-500 uppercase">Paid Revenue (USD eq.)</span>
            </div>
            <p className="text-2xl font-bold text-zinc-50 tabular-nums">
              {fmtUsd(clients.reduce((sum, c) => sum + (rollups[c.id]?.paid_usd || 0), 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Type filter + Search */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-900/50 p-0.5 text-xs shrink-0">
          {[
            { val: "any", label: "All" },
            { val: "sales", label: "Sales" },
            { val: "crm", label: "CRM" },
            { val: "support", label: "Support" },
            { val: "legacy", label: "Legacy" },
          ].map((opt) => (
            <button
              key={opt.val}
              onClick={() => setTypeFilter(opt.val)}
              className={cn(
                "px-3 py-1 rounded transition-colors font-medium flex items-center gap-1.5",
                typeFilter === opt.val
                  ? "bg-orange-500 text-white"
                  : "text-zinc-400 hover:text-zinc-200"
              )}
            >
              {opt.label}
              <span className={cn(
                "text-[10px] tabular-nums",
                typeFilter === opt.val ? "text-orange-100" : "text-zinc-500"
              )}>
                {typeCounts[opt.val] === null ? "—" : typeCounts[opt.val]}
              </span>
            </button>
          ))}
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search clients by name, company, email, or country..."
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50"
            data-testid="client-search-input"
          />
        </div>
      </div>

      {/* Clients Table */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400">Client</TableHead>
                <TableHead className="text-zinc-400">Contact</TableHead>
                <TableHead className="text-zinc-400">Country</TableHead>
                <TableHead className="text-zinc-400">Portfolio</TableHead>
                <TableHead className="text-zinc-400 text-right">Revenue (paid)</TableHead>
                <TableHead className="text-zinc-400">Manager</TableHead>
                <TableHead className="text-zinc-400 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-zinc-500">
                    No clients found
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((client) => (
                  <TableRow key={client.id} className="border-zinc-800 hover:bg-zinc-800/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <ClientLogo
                          src={rollups[client.id]?.logo_url}
                          variant={rollups[client.id]?.logo_variant}
                          name={client.name}
                        />
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              type="button"
                              onClick={() => setProfileClient(client)}
                              className="text-zinc-50 font-medium hover:text-amber-300 transition-colors text-left"
                            >
                              {client.name}
                            </button>
                            {(() => {
                              const tags = client.client_types || [client.type].flat().filter(Boolean) || [];
                              return tags.map((t) => (
                                <Badge key={t} className={cn("text-[10px] uppercase tracking-wide", TAG_STYLE[t] || "bg-zinc-700 text-zinc-300")}>
                                  {t}
                                </Badge>
                              ));
                            })()}
                          </div>
                          {(() => {
                            const profiles = client.billing_profiles || [];
                            const defaultLegal = (profiles.find((p) => p.is_default) || profiles[0])?.legal_name;
                            const subtitle = defaultLegal || client.legal_name || client.company_name;
                            const extra = profiles.length > 1 ? ` +${profiles.length - 1}` : "";
                            return subtitle ? (
                              <p className="text-xs text-zinc-500">{subtitle}{extra}</p>
                            ) : null;
                          })()}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {client.contact_person && (
                          <p className="text-zinc-300 text-sm">{client.contact_person}</p>
                        )}
                        {client.email && (
                          <p className="text-zinc-500 text-xs flex items-center gap-1">
                            <Mail className="h-3 w-3" />{client.email}
                          </p>
                        )}
                        {client.phone && (
                          <p className="text-zinc-500 text-xs flex items-center gap-1">
                            <Phone className="h-3 w-3" />{client.phone}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {client.country && (
                        <Badge variant="outline" className="bg-zinc-800 text-zinc-300 border-zinc-700">
                          {client.country}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const r = rollups[client.id];
                        if (!r) return <span className="text-zinc-600 text-xs">—</span>;
                        const parts = [];
                        if (r.projects_count) parts.push(`${r.projects_count} proj`);
                        if (r.portfolio_count) parts.push(`${r.portfolio_count} works`);
                        const years =
                          r.first_year && r.last_year
                            ? r.first_year === r.last_year
                              ? `${r.first_year}`
                              : `${r.first_year}–${r.last_year}`
                            : null;
                        return (
                          <div className="text-xs">
                            <span className="text-zinc-300">{parts.length ? parts.join(" · ") : "—"}</span>
                            {years && <span className="text-zinc-500 ml-1.5 tabular-nums">{years}</span>}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right">
                      {(() => {
                        const r = rollups[client.id];
                        if (!r || !r.revenue_usd) return <span className="text-zinc-600 text-xs">—</span>;
                        return (
                          <div className="text-xs tabular-nums">
                            <span className="text-emerald-400">{fmtUsd(r.paid_usd)}</span>
                            <span className="text-zinc-500"> / {fmtUsd(r.revenue_usd)}</span>
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {client.sales_manager_name || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setProfileClient(client)}
                          className="text-zinc-400 hover:text-amber-300"
                          data-testid={`profile-client-${client.id}`}
                          title="Portfolio & financials"
                        >
                          <Briefcase className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(client)}
                          className="text-zinc-400 hover:text-zinc-50"
                          data-testid={`edit-client-${client.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-zinc-400 hover:text-red-400"
                                data-testid={`delete-client-${client.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-zinc-50">Delete Client?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete "{client.name}"? This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-50 hover:bg-zinc-700">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(client.id)}
                                  className="bg-red-500 hover:bg-red-600"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Client profile: Portfolio + Financials */}
      <ClientProfileDialog
        clientId={profileClient?.id}
        clientName={profileClient?.name}
        open={!!profileClient}
        onClose={() => setProfileClient(null)}
        canEdit={["admin", "pm_lead", "sales", "sales_director", "crm_manager"].includes(user?.role)}
      />

      {/* Logo library — every mark we hold, linked or not */}
      <ClientLogoLibrary
        open={logoLibraryOpen}
        onClose={() => setLogoLibraryOpen(false)}
        clients={clients}
        canEdit={["admin", "pm_lead", "sales", "sales_director", "crm_manager", "art_director"].includes(user?.role)}
        onChanged={fetchRollups}
      />
    </div>
  );
}
